import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Wrench, Lock, ArrowRight, Loader2 } from "lucide-react";

const LOGO = "/assets/logo-detmold.png";
const SKYLINE = "/assets/skyline-baustelle.png";

export default function MaintenancePage({ settings, onUnlock }) {
  const [password, setPassword] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function verify(e) {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/site/maintenance/verify", { password });
      localStorage.setItem("maintenance_bypass", data.token);
      onUnlock();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Falsches Passwort");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--brand-soft))] flex flex-col" data-testid="maintenance-page">
      {/* Header */}
      <header className="px-6 sm:px-10 py-6 flex items-center gap-3">
        <img src={LOGO} alt="Stadt Detmold" className="h-9 w-auto" />
        <span className="text-sm font-medium text-[hsl(var(--brand-green-dark))] tracking-wide">Quartier am Hornschen Tor</span>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-xl w-full text-center">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[hsl(var(--brand-green))]/15 mb-8">
            <Wrench className="h-7 w-7 text-[hsl(var(--brand-green-strong))]" strokeWidth={1.6} />
          </div>

          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-3">Wartungsmodus</p>
          <h1 className="font-semibold text-4xl sm:text-5xl mb-5 text-[hsl(var(--brand-ink))] leading-tight">
            {settings?.maintenance_title || "Wir sind gleich wieder da"}
          </h1>
          <p className="text-neutral-600 leading-relaxed max-w-md mx-auto mb-3">
            {settings?.maintenance_message || "Die Webseite wird gerade aktualisiert. Bitte schauen Sie in Kürze erneut vorbei."}
          </p>
          {settings?.maintenance_until && (
            <p className="text-sm text-neutral-500 mb-2">
              Voraussichtlich erreichbar ab: <strong className="text-neutral-700">{settings.maintenance_until}</strong>
            </p>
          )}

          {/* Optional bypass for staff */}
          <div className="mt-12 pt-8 border-t border-brand-line max-w-sm mx-auto">
            {!showInput ? (
              <button
                onClick={() => setShowInput(true)}
                data-testid="maintenance-show-login"
                className="inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-[hsl(var(--brand-green-strong))]"
              >
                <Lock className="h-3 w-3" /> Mitarbeiter-Zugang
              </button>
            ) : (
              <form onSubmit={verify} className="text-left">
                <label className="block">
                  <span className="block text-xs uppercase tracking-[0.16em] text-neutral-500 mb-2">Passwort</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="password"
                      autoFocus
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="maintenance-password"
                      placeholder="••••••••"
                      className="flex-1 border border-brand-line bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))]"
                    />
                    <button
                      type="submit"
                      disabled={loading || !password}
                      data-testid="maintenance-submit"
                      className="inline-flex items-center gap-1.5 bg-[hsl(var(--brand-green-dark))] text-white px-4 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))] disabled:opacity-50"
                    >
                      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </label>
                {error && <p className="mt-2 text-xs text-red-600" data-testid="maintenance-error">{error}</p>}
              </form>
            )}
          </div>
        </div>
      </main>

      {/* Skyline footer */}
      <footer className="opacity-70" aria-hidden>
        <img src={SKYLINE} alt="" className="w-full h-auto block" />
      </footer>
    </div>
  );
}
